// @ts-check
// Página do modelo de e-mail (/campaigns/templates/email/:id e /new; spec/email.md seções 8.4 e 19.2): voltar, nome
// editável no lugar, situação da gravação, desfazer e refazer, "Prévia", "Enviar teste" e o menu (Detalhes, Duplicar,
// Converter, Arquivar). Grava sozinha 1,5 s depois da última mudança com `expectedUpdatedAt`; quando outra pessoa
// gravou antes, a faixa de conflito oferece recarregar a versão salva ou manter a sua. Modelo de serviço sem a flag
// WORKSPACE abre só para leitura, com "Duplicar como marketing" (RISCO-11). Com ?from= (seletor de modelo do fluxo), o
// voltar leva de volta para lá com o id do modelo.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { navigate, searchParamsState } from '../../app/router.js';
import {
  convertEmailTemplate, duplicateEmailTemplate, getEmailTemplate, setEmailTemplateArchived, updateEmailTemplate,
} from '../../data/email-templates.js';
import { ApiError } from '../../data/api-client.js';
import { hasCurrentPermissionFlag } from '../../data/metadata-store.js';
import { loadWorkspaceMembers, workspaceMembersState } from '../../data/workspace-members.js';
import { readReturnPath } from '../../lib/return-path.js';
import { formatFullName } from '../../lib/field-metadata.js';
import { formatTime } from '../../lib/format.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { Dropdown, DropdownMenuItems, DropdownMenuSeparator, MenuItem } from '../../ui/dropdown.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { useIsMobile } from '../../ui/hooks.js';
import { enqueueSnackbar, showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { PageCard } from '../page/page-card.js';
import { EmailContentEditor, useLocalEmailPreview } from '../email-design/email-content-editor.js';
import { useEmailHistory } from '../email-design/email-document-store.js';
import { EmailPreviewDialog } from '../email-design/email-preview-frame.js';
import { EmailTemplateTestDialog } from '../email-design/template-test-dialog.js';
import { CreateEmailTemplateDialog } from '../email-design/template-create-dialog.js';
import { EmailDialog } from '../email-design/email-dialog.js';
import { TextField } from '../email-design/property-controls.js';
import { useEmailBranding } from '../email-design/email-branding.js';
import { EMAIL_TEMPLATE_CATEGORIES } from '../../../../shared/email-design/template-categories.js';
import { emailTemplatePath } from './email-templates-tab.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/email-templates.js').EmailTemplate} EmailTemplate
 * @typedef {import('../email-design/email-content-editor.js').EmailContentValue} EmailContentValue
 */

const SAVE_DELAY_MILLISECONDS = 1500;
const TEMPLATES_PATH = '/campaigns/templates?tab=email';
// avisos que só o servidor calcula (tamanho das imagens da biblioteca, campos que o papel não lê)
const SERVER_ONLY_WARNINGS = new Set(['IMAGE_HEAVY', 'VARIABLE_HIDDEN']);

/** @param {EmailTemplate} template @returns {EmailContentValue} */
export const contentValueOf = (template) => ({
  editorMode: template.editorMode, document: template.document, subject: template.subject, preheader: template.preheader, purpose: template.purpose,
});

/**
 * Caminho de volta com o id do modelo (o fluxo que pediu "Criar modelo" escolhe o modelo novo).
 * @param {string} returnPath @param {string} templateId
 */
export function returnPathWithTemplate(returnPath, templateId) {
  const [path, query = ''] = returnPath.split('?');
  const parameters = new URLSearchParams(query);
  parameters.set('emailTemplateId', templateId);
  return `${path}?${parameters.toString()}`;
}

/**
 * Texto da recusa da gravação que a escola consegue corrigir: variável que não existe, campo que o papel não lê,
 * modelo de serviço travado.
 * @param {unknown} error
 * @returns {string | null}
 */
export function saveErrorMessage(error) {
  if (!(error instanceof ApiError)) return null;
  const list = (/** @type {unknown} */ value) => (Array.isArray(value) ? value.map(String).join(', ') : '');
  if (error.code === 'UNKNOWN_CAMPAIGN_VARIABLES') return t('emailTemplates.editor.unknownVariables', { list: list(error.details?.unknownVariables) });
  if (error.code === 'PERMISSION_DENIED' && Array.isArray(error.details?.forbiddenVariables)) {
    return t('emailTemplates.editor.forbiddenVariables', { list: list(error.details.forbiddenVariables) });
  }
  if (error.code === 'PERMISSION_DENIED' && error.details?.reason === 'SERVICE_TEMPLATE_LOCKED') return t('emailTemplates.editor.serviceLocked');
  return null;
}

/** @param {string | null | undefined} workspaceMemberId */
function memberName(workspaceMemberId) {
  const member = (workspaceMembersState.value ?? []).find((item) => item.id === workspaceMemberId);
  return member ? formatFullName(member.name) || member.userEmail || '' : '';
}

/**
 * Detalhes do modelo: nome, descrição, categoria e finalidade (Serviço só com WORKSPACE).
 * @param {{ template: EmailTemplate, onClose: () => void, onSaved: (template: EmailTemplate) => void, readOnly: boolean }} props
 */
function TemplateDetailsDialog({ template, onClose, onSaved, readOnly }) {
  const [description, setDescription] = useState(template.description ?? '');
  const [category, setCategory] = useState(template.category);
  const [purpose, setPurpose] = useState(template.purpose);
  const [isSaving, setIsSaving] = useState(false);
  const canChangePurpose = hasCurrentPermissionFlag('WORKSPACE') && !readOnly;
  const save = async () => {
    setIsSaving(true);
    try {
      /** @type {Record<string, unknown>} */
      const patch = { expectedUpdatedAt: template.updatedAt };
      if (description !== (template.description ?? '')) patch.description = description;
      if (category !== template.category) patch.category = category;
      if (purpose !== template.purpose) patch.purpose = purpose;
      onSaved(await updateEmailTemplate(template.id, patch));
      onClose();
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsSaving(false);
    }
  };
  return html`<${EmailDialog} title=${t('emailTemplates.details.title')} onClose=${onClose} className="email-details-dialog"
    footer=${readOnly
      ? html`<${Button} title=${t('common.close')} variant="secondary" size="small" onClick=${onClose} />`
      : html`<${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${onClose} />
          <${Button} title=${t('common.save')} accent="blue" size="small" isLoading=${isSaving} onClick=${save} />`}>
    <${TextField} label=${t('emailTemplates.details.description')} value=${description} multiline=${true} rows=${3} maxLength=${500} disabled=${readOnly} onChange=${setDescription} />
    <label class="input-label" for="email-template-category">${t('emailTemplates.details.category')}</label>
    <select id="email-template-category" class="text-input email-select" value=${category} disabled=${readOnly}
      onChange=${(/** @type {Event} */ event) => setCategory(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
      ${EMAIL_TEMPLATE_CATEGORIES.map((key) => html`<option value=${key}>${t(`emailTemplates.categories.${key}`)}</option>`)}
    </select>
    <fieldset class="email-purpose-field" disabled=${!canChangePurpose}>
      <legend class="input-label">${t('emailTemplates.details.purpose')}</legend>
      ${(/** @type {const} */ (['MARKETING', 'SERVICE'])).map((value) => html`<label class="email-radio-row">
        <input type="radio" name="email-template-purpose" value=${value} checked=${purpose === value} onChange=${() => setPurpose(value)} />
        <span><strong>${t(`emailTemplates.purposes.${value}`)}</strong><span class="input-hint">${t(`emailTemplates.purposes.${value === 'SERVICE' ? 'serviceHint' : 'marketingHint'}`)}</span></span>
      </label>`)}
      ${canChangePurpose ? null : html`<span class="input-hint">${t('emailTemplates.purposes.serviceNeedsWorkspace')}</span>`}
    </fieldset>
  <//>`;
}

/**
 * Nome editável no lugar.
 * @param {{ name: string, readOnly: boolean, onRename: (name: string) => Promise<boolean> }} props
 */
function InlineName({ name, readOnly, onRename }) {
  const [draft, setDraft] = useState(name);
  const [isEditing, setIsEditing] = useState(false);
  useEffect(() => {
    if (!isEditing) setDraft(name);
  }, [name]);
  const commit = async () => {
    const next = draft.trim();
    setIsEditing(false);
    if (!next || next === name) {
      setDraft(name);
      return;
    }
    if (!(await onRename(next))) setDraft(name);
  };
  if (readOnly) return html`<h1 class="email-template-name">${name}</h1>`;
  return html`<input
    class="email-template-name-input"
    value=${draft}
    maxLength=${120}
    aria-label=${t('emailTemplates.editor.name')}
    onFocus=${() => setIsEditing(true)}
    onInput=${(/** @type {Event} */ event) => setDraft(/** @type {HTMLInputElement} */ (event.currentTarget).value)}
    onBlur=${commit}
    onKeyDown=${(/** @type {KeyboardEvent} */ event) => {
      if (event.key === 'Enter') /** @type {HTMLInputElement} */ (event.currentTarget).blur();
      if (event.key === 'Escape') {
        setDraft(name);
        /** @type {HTMLInputElement} */ (event.currentTarget).blur();
      }
    }}
  />`;
}

/**
 * Editor de um modelo já carregado.
 * @param {{ initialTemplate: EmailTemplate, returnPath: string | null }} props
 */
function TemplateEditor({ initialTemplate, returnPath }) {
  const isMobile = useIsMobile();
  const branding = useEmailBranding();
  const [template, setTemplate] = useState(initialTemplate);
  const readOnly = template.canEdit === false;
  const history = useEmailHistory(contentValueOf(initialTemplate), { enabled: !readOnly });
  const [saveState, setSaveState] = useState(/** @type {'IDLE' | 'PENDING' | 'SAVING' | 'SAVED' | 'FAILED'} */ ('IDLE'));
  const [savedAt, setSavedAt] = useState(/** @type {string | null} */ (null));
  const [conflict, setConflict] = useState(/** @type {{ current: EmailTemplate, savedBy: string | null } | null} */ (null));
  const [dialog, setDialog] = useState(/** @type {'PREVIEW' | 'TEST' | 'DETAILS' | 'CONVERT' | null} */ (null));
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuAnchorRef = useRef(/** @type {HTMLElement | null} */ (null));
  const savedValueRef = useRef(contentValueOf(initialTemplate));
  const templateRef = useRef(template);
  templateRef.current = template;
  const valueRef = useRef(history.value);
  valueRef.current = history.value;
  const timerRef = useRef(/** @type {ReturnType<typeof setTimeout> | null} */ (null));
  const savingRef = useRef(/** @type {Promise<boolean> | null} */ (null));
  const preview = useLocalEmailPreview(history.value, { settings: branding.settings, institutionName: branding.institutionName, logoUrl: branding.logoUrl });

  useEffect(() => {
    loadWorkspaceMembers();
  }, []);

  const isDirty = () => {
    const saved = savedValueRef.current;
    const current = valueRef.current;
    return saved.document !== current.document || saved.subject !== current.subject || saved.preheader !== current.preheader;
  };

  /** Grava o que mudou; devolve se ficou tudo salvo. @returns {Promise<boolean>} */
  const save = async () => {
    if (readOnly || conflict) return false;
    if (savingRef.current) await savingRef.current;
    if (!isDirty()) return true;
    const sent = valueRef.current;
    const saved = savedValueRef.current;
    /** @type {Record<string, unknown>} */
    const patch = { expectedUpdatedAt: templateRef.current.updatedAt };
    if (sent.document !== saved.document) patch.document = sent.document;
    if (sent.subject !== saved.subject) patch.subject = sent.subject;
    if (sent.preheader !== saved.preheader) patch.preheader = sent.preheader;
    setSaveState('SAVING');
    const request = updateEmailTemplate(templateRef.current.id, patch)
      .then((updated) => {
        savedValueRef.current = sent;
        setTemplate((current) => ({ ...updated, canEdit: current.canEdit }));
        setSavedAt(updated.updatedAt);
        setSaveState(isDirty() ? 'PENDING' : 'SAVED');
        return !isDirty();
      })
      .catch((error) => {
        if (error instanceof ApiError && error.code === 'EDIT_CONFLICT' && error.details?.current) {
          setConflict({ current: error.details.current, savedBy: error.details.savedBy ?? null });
          setSaveState('IDLE');
        } else {
          setSaveState('FAILED');
          const message = saveErrorMessage(error);
          if (message) enqueueSnackbar({ variant: 'error', message });
          else if (!(error instanceof ApiError) || error.statusCode !== 0) showErrorSnackbar(error);
        }
        return false;
      })
      .finally(() => {
        savingRef.current = null;
      });
    savingRef.current = request;
    const result = await request;
    // o que mudou durante a gravação vai na próxima
    if (result === false || isDirty()) scheduleSave();
    return result;
  };
  const scheduleSave = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (isDirty()) save();
    }, SAVE_DELAY_MILLISECONDS);
  };
  /** Grava agora (antes do teste, da conversão e de sair). */
  const flush = async () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    return isDirty() ? save() : true;
  };

  useEffect(() => {
    if (readOnly || conflict || !isDirty()) return;
    setSaveState((state) => (state === 'SAVING' ? state : 'PENDING'));
    scheduleSave();
  }, [history.value]);

  useEffect(() => {
    /** @param {BeforeUnloadEvent} event */
    const warn = (event) => {
      if (!isDirty() || readOnly) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
      if (timerRef.current) clearTimeout(timerRef.current);
      // sair da página com mudança pendente: grava sem esperar
      if (isDirty() && !readOnly) save();
    };
  }, []);

  const goBack = async () => {
    await flush();
    navigate(returnPath ? returnPathWithTemplate(returnPath, template.id) : TEMPLATES_PATH);
  };

  /** @param {string} name */
  const rename = async (name) => {
    try {
      const updated = await updateEmailTemplate(template.id, { name, expectedUpdatedAt: templateRef.current.updatedAt });
      setTemplate((current) => ({ ...updated, canEdit: current.canEdit }));
      return true;
    } catch (error) {
      if (error instanceof ApiError && error.code === 'EDIT_CONFLICT' && error.details?.current) {
        setConflict({ current: error.details.current, savedBy: error.details.savedBy ?? null });
      } else showErrorSnackbar(error);
      return false;
    }
  };

  const reloadSaved = () => {
    if (!conflict) return;
    const current = conflict.current;
    savedValueRef.current = contentValueOf(current);
    setTemplate((previous) => ({ ...current, canEdit: previous.canEdit }));
    history.reset(contentValueOf(current));
    setConflict(null);
    setSaveState('SAVED');
    setSavedAt(current.updatedAt);
  };
  const keepMine = () => {
    if (!conflict) return;
    const current = conflict.current;
    // a minha versão vale sobre a gravada: o próximo PATCH leva a data dela
    setTemplate((previous) => ({ ...previous, updatedAt: current.updatedAt }));
    templateRef.current = { ...templateRef.current, updatedAt: current.updatedAt };
    savedValueRef.current = contentValueOf(current);
    setConflict(null);
    requestAnimationFrame(() => save());
  };

  const duplicate = async () => {
    try {
      await flush();
      const copy = await duplicateEmailTemplate(template.id);
      showSuccessSnackbar(t('emailTemplates.duplicated', { name: copy.name }));
      navigate(emailTemplatePath(copy.id));
    } catch (error) {
      showErrorSnackbar(error);
    }
  };
  const toggleArchive = async () => {
    try {
      const updated = await setEmailTemplateArchived(template.id, !template.archivedAt);
      setTemplate((current) => ({ ...updated, canEdit: current.canEdit }));
      showSuccessSnackbar(updated.archivedAt ? t('emailTemplates.archivedDone') : t('emailTemplates.unarchived'));
    } catch (error) {
      showErrorSnackbar(error);
    }
  };
  const convertTarget = template.editorMode === 'RICH_TEXT' ? 'BLOCKS' : 'HTML';
  /** @param {'BLOCKS' | 'HTML'} toMode */
  const convert = async (toMode) => {
    setDialog(null);
    try {
      if (!(await flush())) return;
      const updated = await convertEmailTemplate(template.id, toMode);
      savedValueRef.current = contentValueOf(updated);
      setTemplate((current) => ({ ...updated, canEdit: current.canEdit }));
      history.reset(contentValueOf(updated));
      showSuccessSnackbar(t('emailTemplates.editor.converted', { mode: t(`emailTemplates.modes.${toMode}`) }));
    } catch (error) {
      showErrorSnackbar(error);
    }
  };

  const statusText = saveState === 'SAVING'
    ? t('emailTemplates.editor.saving')
    : saveState === 'PENDING'
      ? t('emailTemplates.editor.unsaved')
      : saveState === 'FAILED'
        ? t('emailTemplates.editor.saveFailed')
        : saveState === 'SAVED' && savedAt
          ? t('emailTemplates.editor.saved', { time: formatTime(savedAt) })
          : '';
  const extraProblems = (template.compiled?.warnings ?? []).filter((warning) => SERVER_ONLY_WARNINGS.has(warning.code));

  return html`<div class="email-template-page-body">
    <header class="email-template-header">
      <${LightIconButton} icon="IconArrowLeft" size="medium" ariaLabel=${returnPath ? t('emailTemplates.editor.backToFlow') : t('emailTemplates.editor.back')} onClick=${goBack} />
      <div class="email-template-titles">
        <${InlineName} name=${template.name} readOnly=${readOnly} onRename=${rename} />
        <span class="email-template-status" role="status" data-state=${saveState}>
          ${statusText}
          ${saveState === 'FAILED' ? html` <button type="button" class="email-link-button" onClick=${() => save()}>${t('emailTemplates.retry')}</button>` : null}
        </span>
      </div>
      <div class="email-template-actions">
        ${readOnly
          ? null
          : html`<${LightIconButton} icon="IconArrowBackUp" size="medium" ariaLabel=${t('emailTemplates.editor.undo')} disabled=${!history.canUndo} onClick=${history.undo} />
              <${LightIconButton} icon="IconArrowForwardUp" size="medium" ariaLabel=${t('emailTemplates.editor.redo')} disabled=${!history.canRedo} onClick=${history.redo} />`}
        <${Button} title=${isMobile ? undefined : t('emailTemplates.preview.title')} ariaLabel=${t('emailTemplates.preview.title')} icon="IconEye" size="small" variant="secondary"
          onClick=${() => setDialog('PREVIEW')} />
        <${Button} title=${isMobile ? undefined : t('emailTemplates.actions.sendTest')} ariaLabel=${t('emailTemplates.actions.sendTest')} icon="IconSend" size="small" variant="secondary"
          onClick=${async () => { await flush(); setDialog('TEST'); }} />
        <span ref=${menuAnchorRef}>
          <${LightIconButton} icon="IconDotsVertical" size="medium" ariaLabel=${t('emailTemplates.editor.more')} onClick=${() => setIsMenuOpen((open) => !open)} />
        </span>
        <${Dropdown} anchorRef=${menuAnchorRef} isOpen=${isMenuOpen} onClose=${() => setIsMenuOpen(false)} placement="bottom-end" width=${230} role="menu">
          <${DropdownMenuItems}>
            <${MenuItem} icon="IconSettings" text=${t('emailTemplates.details.title')} onClick=${() => { setIsMenuOpen(false); setDialog('DETAILS'); }} />
            <${MenuItem} icon="IconCopy" text=${t('emailTemplates.actions.duplicate')} onClick=${() => { setIsMenuOpen(false); duplicate(); }} />
            ${!readOnly && template.editorMode !== 'HTML'
              ? html`<${MenuItem} icon="IconSwitchHorizontal" text=${t('emailTemplates.actions.convertTo', { mode: t(`emailTemplates.modes.${convertTarget}`) })}
                  onClick=${() => { setIsMenuOpen(false); setDialog('CONVERT'); }} />`
              : null}
            <${DropdownMenuSeparator} />
            <${MenuItem} icon=${template.archivedAt ? 'IconRestore' : 'IconArchive'}
              text=${template.archivedAt ? t('emailTemplates.actions.unarchive') : t('emailTemplates.actions.archive')}
              onClick=${() => { setIsMenuOpen(false); toggleArchive(); }} />
          <//>
        <//>
      </div>
    </header>
    ${readOnly
      ? html`<div class="email-readonly-banner" role="status">
          <${Icon} name="IconLock" size=${14} />
          <span>${t('emailTemplates.editor.serviceLocked')}</span>
          <${Button} title=${t('emailTemplates.editor.duplicateAsMarketing')} icon="IconCopy" size="small" variant="secondary" onClick=${duplicate} />
        </div>`
      : null}
    ${template.archivedAt ? html`<div class="email-info-banner" role="status"><${Icon} name="IconArchive" size=${14} /><span>${t('emailTemplates.editor.archivedNotice')}</span></div>` : null}
    ${template.usage?.liveFlows
      ? html`<div class="email-info-banner" role="status"><${Icon} name="IconInfoCircle" size=${14} /><span>${t('emailTemplates.editor.usedInLiveFlows', { count: template.usage.liveFlows })}</span></div>`
      : null}
    ${conflict
      ? html`<div class="email-conflict-banner" role="alert">
          <${Icon} name="IconAlertTriangle" size=${16} />
          <div class="email-conflict-texts">
            <strong>${t('emailTemplates.editor.conflictTitle')}</strong>
            <span>${conflict.savedBy && memberName(conflict.savedBy) ? t('emailTemplates.editor.conflictBy', { name: memberName(conflict.savedBy) }) : t('emailTemplates.editor.conflictDescription')}</span>
          </div>
          <${Button} title=${t('emailTemplates.editor.conflictReload')} size="small" variant="secondary" onClick=${reloadSaved} />
          <${Button} title=${t('emailTemplates.editor.conflictKeepMine')} size="small" accent="blue" onClick=${keepMine} />
        </div>`
      : null}
    <${EmailContentEditor}
      value=${history.value}
      onChange=${(/** @type {EmailContentValue} */ next, /** @type {{ coalesceKey?: string } | undefined} */ options) => history.set(next, options)}
      context="TEMPLATE"
      readOnly=${readOnly}
      brandColor=${branding.brandColor}
      logoUrl=${branding.logoUrl}
      settings=${branding.settings}
      institutionName=${branding.institutionName}
      extraProblems=${extraProblems}
    />
    ${dialog === 'PREVIEW'
      ? html`<${EmailPreviewDialog} onClose=${() => setDialog(null)} senderName=${branding.settings?.fromName || branding.institutionName}
          local=${{ html: preview.html, text: preview.text, subject: preview.subject, preheader: preview.preheader, estimatedHeight: preview.compiled.estimatedHeight }}
          request=${{ document: history.value.document, subject: history.value.subject, preheader: history.value.preheader, purpose: history.value.purpose }} />`
      : null}
    ${dialog === 'TEST'
      ? html`<${EmailTemplateTestDialog} templateId=${template.id} templateName=${template.name} hasErrors=${preview.compiled.errors.length > 0} onClose=${() => setDialog(null)} />`
      : null}
    ${dialog === 'DETAILS'
      ? html`<${TemplateDetailsDialog} template=${template} readOnly=${readOnly} onClose=${() => setDialog(null)}
          onSaved=${(/** @type {EmailTemplate} */ updated) => {
            savedValueRef.current = { ...savedValueRef.current, purpose: updated.purpose };
            setTemplate((current) => ({ ...updated, canEdit: current.canEdit }));
            history.reset({ ...valueRef.current, purpose: updated.purpose });
          }} />`
      : null}
    ${dialog === 'CONVERT'
      ? html`<${Modal} title=${t('emailTemplates.editor.convertTitle', { mode: t(`emailTemplates.modes.${convertTarget}`) })}
          subtitle=${convertTarget === 'HTML' ? t('emailTemplates.editor.convertToHtmlWarning') : t('emailTemplates.editor.convertConfirm')}
          confirmLabel=${t('emailTemplates.actions.convert')} onClose=${() => setDialog(null)} onConfirm=${() => convert(convertTarget)} />`
      : null}
  </div>`;
}

/**
 * Modelo de e-mail novo ou existente (/campaigns/templates/email/new e /campaigns/templates/email/:templateId).
 * @param {{ templateId: string | null }} props
 */
export function EmailTemplatePage({ templateId }) {
  const ownPath = templateId ? emailTemplatePath(templateId) : '/campaigns/templates/email/new';
  const [returnPath] = useState(() => readReturnPath(searchParamsState.value.get('from'), ownPath));
  const [template, setTemplate] = useState(/** @type {EmailTemplate | null} */ (null));
  const [loadError, setLoadError] = useState(/** @type {'NOT_FOUND' | 'FAILED' | null} */ (null));

  const load = () => {
    if (!templateId) return;
    setLoadError(null);
    setTemplate(null);
    getEmailTemplate(templateId)
      .then((loaded) => {
        setTemplate(loaded);
        document.title = `${loaded.name} - ${t('emailTemplates.title')}`;
      })
      .catch((error) => setLoadError(error instanceof ApiError && (error.statusCode === 404 || error.code === 'RECORD_NOT_FOUND') ? 'NOT_FOUND' : 'FAILED'));
  };
  useEffect(load, [templateId]);

  const leave = () => navigate(returnPath ?? TEMPLATES_PATH);

  let body;
  if (!templateId) {
    body = html`<div class="email-template-new">
      <${CreateEmailTemplateDialog} onClose=${leave}
        onCreated=${(/** @type {EmailTemplate} */ created) => navigate(`${emailTemplatePath(created.id)}${returnPath ? `?from=${encodeURIComponent(returnPath)}` : ''}`, { replace: true })} />
    </div>`;
  } else if (loadError === 'NOT_FOUND') {
    body = html`<div class="email-state">
      <${EmptyState} icon="IconMail" title=${t('emailTemplates.editor.notFound')}
        action=${html`<${Button} title=${t('emailTemplates.editor.backToTemplates')} icon="IconArrowLeft" variant="secondary" size="small" onClick=${() => navigate(TEMPLATES_PATH)} />`} />
    </div>`;
  } else if (loadError === 'FAILED') {
    body = html`<div class="email-state">
      <${EmptyState} icon="IconAlertTriangle" title=${t('emailTemplates.loadError')}
        action=${html`<${Button} title=${t('emailTemplates.retry')} variant="secondary" size="small" onClick=${load} />`} />
    </div>`;
  } else if (!template) {
    body = html`<div class="email-template-loading" aria-busy="true">
      <${Skeleton} width="40%" height=${24} />
      <${Skeleton} height=${36} />
      <div class="email-template-loading-canvas"><${Skeleton} width=${600} height=${520} radius=${4} /></div>
    </div>`;
  } else {
    body = html`<${TemplateEditor} key=${template.id} initialTemplate=${template} returnPath=${returnPath} />`;
  }
  return html`<${PageCard} className="campaigns-screen email-template-page">${body}<//>`;
}
